<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import PurchaseProjectItemBadge from './PurchaseProjectItemBadge.vue';
import EditPurchaseProjectItemSheet from './EditPurchaseProjectItemSheet.vue';
import { mockProducts } from './story/fixtures.js';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update', 'remove']);

const { t } = useI18n();

const editSheetRef = ref(null);

const product = computed(
  () => mockProducts.find(p => p.id === props.item.productId) ?? null
);

const currentVariant = computed(
  () => product.value?.variants.find(v => v.id === props.item.variantId) ?? null
);

const formatCurrency = amount =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: product.value?.currency ?? 'EUR',
  }).format(amount);

const formattedPrice = computed(() =>
  product.value ? formatCurrency(product.value.price) : ''
);

const formattedCompareAtPrice = computed(() => {
  const p = product.value;
  if (!p?.compareAtPrice || p.compareAtPrice <= p.price) return null;
  return formatCurrency(p.compareAtPrice);
});
</script>

<template>
  <div v-if="product" class="flex gap-3 py-3">
    <img
      :src="product.image"
      :alt="product.title"
      class="size-14 shrink-0 rounded-lg object-cover bg-n-alpha-1"
    />

    <div class="flex flex-col flex-1 min-w-0 gap-1.5">
      <div class="flex items-start justify-between gap-2">
        <div class="flex flex-col min-w-0">
          <div class="flex items-center gap-1.5 flex-wrap">
            <span class="text-sm font-medium text-n-slate-12 leading-snug">
              {{ product.title }}
            </span>
            <span
              v-if="item.isRecommended"
              class="inline-flex items-center gap-1 rounded-full bg-amber-50 dark:bg-amber-950 px-1.5 py-0.5 text-xs font-medium text-amber-600 dark:text-amber-400"
            >
              <span class="i-lucide-star size-3 fill-current" />
              {{ t('PURCHASE_PROJECTS.ITEMS.RECOMMENDED') }}
            </span>
          </div>
          <div class="flex items-center gap-1.5 flex-wrap mt-0.5">
            <span class="text-xs text-n-slate-10">{{
              currentVariant?.label ?? product.sku
            }}</span>
            <PurchaseProjectItemBadge :stock-status="product.stockStatus" />
          </div>
          <span class="text-xs text-n-slate-10 mt-0.5">
            {{ t('PURCHASE_PROJECTS.ITEMS.QUANTITY') }} {{ item.quantity ?? 1 }}
          </span>
          <p
            v-if="item.sellerNote"
            class="text-xs text-n-slate-10 italic leading-snug mt-0.5"
          >
            {{ item.sellerNote }}
          </p>
        </div>

        <div class="flex items-center gap-1 shrink-0">
          <div class="flex flex-col items-end">
            <span class="text-sm font-semibold text-n-slate-12">
              {{ formattedPrice }}
            </span>
            <span
              v-if="formattedCompareAtPrice"
              class="text-xs text-n-slate-9 line-through"
            >
              {{ formattedCompareAtPrice }}
            </span>
          </div>
          <Button
            variant="ghost"
            color="slate"
            size="xs"
            icon="i-lucide-pencil"
            @click="editSheetRef?.open(item)"
          />
        </div>
      </div>
    </div>

    <EditPurchaseProjectItemSheet
      ref="editSheetRef"
      @update="updatedItem => emit('update', updatedItem)"
      @remove="removedItem => emit('remove', removedItem)"
    />
  </div>
</template>
